<template>
  <div class="bg-white mt-5 rounded p-3 px-5">
    <div class="w-full flex flex-col md:flex-row border-b py-5">
      <!-- start of search / stats header -->

      <div
        class="w-32 h-32 p-4 -mt-20 ml-2 mb-2 text-white rounded-lg relative flex-no-shrink mr-5"
        :class="page.icon_color ? page.icon_color : 'bg-tertiary'"
      >
        <icon name="videoettes"></icon>
      </div>

      <div class="w-full flex justify-between">
        <h1 class="text-xl self-end mb-3 uppercase font-bold">
          My Branded Videos
        </h1>
      </div>
    </div>
    <!-- end of search / stats header -->
    <div v-if="showExpiringVideos" class="border border-orange-lighter">
      <div class="w-full p-3 bg-orange-lighter">
        <h4>Videos about to expire</h4>
        <div class="my-2">Below videos will be expired within a month. Click 'Keep Video' to confirm those are still required.</div>
        <div class="my-2 text-red"> If a video has not been marked as still required, it will be removed on its expiry date.</div>
      </div>
      <div class="w-full p-3 overflow-x-scroll">
        <table class="w-full min-w-288">
          <thead>
          <tr class="text-xs uppercase">
            <th class="p-3 text-left">File name</th>
            <th class="p-3">Created</th>
            <th class="p-3">Status</th>
            <th class="p-3">Expiry Date</th>
            <th class="p-3">Action</th>
          </tr>
          </thead>

          <tbody v-if="expiringVideos && expiringVideos.length > 0">
          <tr
            v-for="(expiringVideo, index) in expiringVideos"
            :key="expiringVideo.id"
            class="text-xs md:text-sm border-b text-center"
            :class="{
              'bg-grey-lightest': index % 2 === 0
            }"
          >
            <td
              class="p-3 w-64 text-left border-l-2"
              :class="{
                'border-green': expiringVideo.status === 'Published',
                'border-orange': expiringVideo.status === 'Queued',
                'border-red-light': expiringVideo.status === 'Rejected'
              }"
            >
              {{ expiringVideo.name }}
            </td>
            <td class="p-3 w-48 text-xs text-grey-dark">
              <template v-if="(created = formatCreatedAt(expiringVideo.created_at))">
                <span class="block">{{ created.date }}</span>
              </template>
              <span v-else class="block">-</span>
            </td>
            <td
              class="p-3 w-48 text-xs"
              :class="{
                'text-green': expiringVideo.status === 'Published',
                'text-orange': expiringVideo.status === 'Queued',
                'text-red-light': expiringVideo.status === 'Rejected'
              }"
            >
              {{ expiringVideo.status }}
            </td>
            <td class="p-3 w-48 text-xs text-grey-dark">
              <span> {{ expiringVideo.expiry_date | date }} </span>
            </td>
            <td class="p-3 w-24">
              <button type="button" class="bg-white border border-tertiary text-tertiary rounded px-2 py-1 text-sm font-medium align-bottom m-2" @click="keepVideo(expiringVideo, index)">
                Keep Video
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <div class="w-full p-3 bg-orange-lighter text-right">
        <button type="button" class="bg-white border border-tertiary text-tertiary rounded px-2 py-1 font-medium align-bottom m-2" @click="showExpiringVideos=false">Show me later</button>
      </div>
    </div>

    <div>
      <div class="flex flex-col md:flex-row mb-3 border-b py-5">
        <!-- start of filters -->
        <div
          class="bg-white md:w-1/4 w-full h-full mb-6 md:mb-0 px-3 flex items-center self-end border rounded overflow-hidden md:mr-5 shadow"
        >
          <i class="fa fa-search text-grey"></i>
          <input
            v-model="search"
            type="text"
            class="py-4 px-2 w-full h-full bg-transparent"
            placeholder="Search"
          />
        </div>

        <div class="flex w-full md:w-1/4 mb-8 md:mb-0 md:border-r md:pr-10">
          <div class="flex w-full flex-col lg:mr-3">
            <label for="include" class="mb-2 text-xs">Status:</label>
            <pl-select
              v-model="status"
              name="filter_menu"
              id="include"
              select-class="bg-white rounded h-10 py-0 shadow text-sm"
              class="lg:w-48 w-full"
            >
              <option :value="null">All</option>
              <option value="queued">Queued</option>
              <option value="complete">Publishing</option>
              <option value="published">Published</option>
              <option value="excluded">Cancelled</option>
              <option value="rejected">Rejected</option>
            </pl-select>
          </div>
        </div>
      </div>
      <!-- end of filters -->

      <div class="w-full flex justify-around text-sm md:hidden">
        <i class="fa fa-arrow-left"></i> Scroll the table left &amp; right to see
        more
        <i class="fa fa-arrow-right"></i>
      </div>

      <div class="w-full p-3 overflow-x-scroll">
        <table class="w-full min-w-288">
          <thead>
          <tr class="text-xs uppercase">
            <!-- <th class="p-3 text-left">Preview</th> -->
            <th class="p-3 text-left">File name</th>
            <th class="p-3">Created</th>
            <th class="p-3">Status</th>
            <th class="p-3">View</th>
            <th class="p-3">Social</th>
            <th class="p-3">Download</th>
          </tr>
          </thead>

          <tbody v-if="videos && videos.length > 0">
          <tr
            v-for="(video, index) in videos"
            :key="video.id"
            class="text-xs md:text-sm border-b text-center"
            :class="{
              'bg-grey-lightest': index % 2 === 0
            }"
          >
            <td
              class="p-3 w-64 text-left border-l-2"
              :class="{
                'border-green': video.status === 'Published',
                'border-orange': video.status === 'Queued',
                'border-red-light': video.status === 'Rejected'
              }"
            >
              {{ video.name }}
            </td>
            <td class="p-3 w-48 text-xs text-grey-dark">
              <template v-if="(created = formatCreatedAt(video.created_at))">
                <span class="block">{{ created.date }}</span>
                <span class="block mt-1">{{ created.time }}</span>
              </template>
              <span v-else class="block">-</span>
            </td>
            <td
              class="p-3 w-48 text-xs"
              :class="{
                'text-green': video.status === 'Published',
                'text-orange': video.status === 'Queued',
                'text-red-light': video.status === 'Rejected'
              }"
            >
              {{ video.status }}
            </td>
            <td class="p-3 w-24">
              <button
                @click="view(video)"
                :class="{ 'cursor-not-allowed': !video.rendered }"
                :disabled="!video.rendered"
              >
                <icon name="video" class="w-10 center-x" :center="false" />
              </button>
            </td>
            <td class="p-3 w-24">
              <button
                @click="shareVideoette(video)"
                :class="{ 'cursor-not-allowed': !video.rendered }"
                :disabled="!video.rendered"
              >
                <icon name="social" class="w-10 center-x" :center="false" />
              </button>
            </td>
            <td class="p-3 w-24">
              <button
                :disabled="!video.rendered"
                :class="{ 'cursor-not-allowed': !video.rendered }"
                @click="download(video)"
              >
                <icon
                  name="download-alt"
                  class="w-10 center-x"
                  :center="false"
                />
              </button>
            </td>
            <td
              class="p-3 w-16 border-r-2"
              :class="{
                'border-green': video.status === 'Published',
                'border-orange': video.status === 'Queued',
                'border-red-light': video.status === 'Rejected'
              }"
            >
              <button
                :disabled="video.status === 'Queued' || video.status === 'Rejected'|| video.status === 'Cleaned' || video.status === 'Publishing'"
                @click="showDeleteModal = true; setSelectedVideoette(video)"
              >
                <i class="far fa-trash-alt fa-fw"></i>
              </button>
            </td>
          </tr>
          </tbody>
          <tbody v-else-if="!loading">
          <tr>
            <td colspan="10" class="text-center pt-8 text-2xl text-grey-dark">
              No Videos Found
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <modal
        v-if="showVideoetteShareModal"
        @leave="showVideoetteShareModal = null"
      >
        <videoshare :videoId="selectedVideoette.video_id"></videoshare>
      </modal>

      <div class="w-full text-sm flex py-3 justify-center">
        <loader v-if="loading"></loader>
        <button
          v-else-if="currentPage < lastPage"
          @click="moreResults"
          class="border-b text-grey-dark"
        >
          SHOW MORE
          <i class="fa fa-caret-down text-xs"></i>
        </button>
      </div>

      <view-video-modal
        :show="viewVideo"
        :video="selectedVideo"
        @close="
        viewVideo = false;
        selectedVideo = null;
      "
      />
      <videoette-download-modal
        :show="downloadVideo"
        :video="selectedVideo"
        @close="
        downloadVideo = false;
        selectedVideo = null;
      "
      />
      <deletion-modal
        v-if="showDeleteModal"
        @confirmed="deleteVideo(selectedVideoette)"
        @leave="showDeleteModal = false"
        @dismissed="showDeleteModal = false">
        <p class="text-center">Are you sure you wish to delete the branded video?</p>
      </deletion-modal>
      <div class="w-full p-2 text-right" v-if="expiringVideos.length>0">
        <button type="button" class="bg-orange-lighter border border-tertiary text-tertiary rounded px-2 py-1 font-medium align-bottom m-2"
                v-if="!showExpiringVideos" @click="showExpiringVideos=true">Show Expiring Videos</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { debounce } from 'lodash'
import { moneyFormat, truncate } from '@/helpers'
import moment from 'moment'
import Videoshare from '@/components/VideoShare'
import VideoetteDownloadModal from '@/product-modules/videoettes/VideoetteDownloadModal'
import ViewVideoModal from '@/product-modules/videoettes/ViewVideoModal'
import api from '@/http'
import DeletionModal from '@/components/Modals/DeletionModal'

export default {
  name: 'MyBrandingVideo',

  props: ['page'],

  components: {
    ViewVideoModal,
    Videoshare,
    VideoetteDownloadModal,
    DeletionModal
  },

  data () {
    return {
      loading: true,
      currentPage: 1,
      lastPage: 1,
      videos: [],
      expiringVideos: [],
      selectedVideo: null,
      department: null,
      status: null,
      search: '',
      selectedVideoette: null,
      viewVideo: false,
      downloadVideo: false,
      showVideoetteViewModal: false,
      showVideoetteShareModal: false,
      showDeleteModal: false,
      showExpiringVideos: false
    }
  },

  computed: {
    ...mapState(['selectedOffice'])
  },

  methods: {
    ...mapActions([
      'displayToastNotification'
    ]),
    fetchVideoettes: debounce(function () {
      this.loading = true
      api
        .get('/branded-video', {
          params: {
            office_id: this.selectedOffice.id,
            status: this.status,
            search: this.search,
            page: this.currentPage
          }
        })
        .then(response => {
          if (this.currentPage === 1) {
            this.videos = response.data.data
          } else {
            this.videos = this.videos.concat(response.data.data)
          }
          this.lastPage = response.data.meta.last_page
        })
        .finally(() => {
          this.loading = false
        })
    }, 500),

    fetchExpiringVideos: function () {
      this.loading = true
      api
        .get('/expiring-branded-video', {
          params: {
            office_id: this.selectedOffice.id
          }
        })
        .then(response => {
          this.expiringVideos = response.data
          if (this.expiringVideos.length > 0) {
            this.showExpiringVideos = true
          }
        })
        .finally(() => {
          this.loading = false
        })
    },

    moreResults () {
      this.currentPage++
      this.fetchVideoettes()
    },

    view (branded) {
      this.selectedVideo = branded
      this.viewVideo = true
    },

    download (branded) {
      this.selectedVideo = branded
      this.downloadVideo = true
    },

    deleteVideo (video) {
      console.log('deleted branded video')
      var videoData = {
        video_id: video.id
      }
      api.delete('/branded-video', { data: videoData })
        .then(response => {
          if (response.status === 200) {
            this.displayToastNotification({
              type: 'success',
              icon: 'fa-check',
              title: 'Video Deleted',
              info: `Video has been successfully deleted.`
            })
          }
        }).catch(({ response }) => {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-exclamation',
            title: 'Error',
            info: response.data.message
          })
        }).finally(() => {
          this.showDeleteModal = false
        })
    },
    getVimeoUrl (vimeoId) {
      return `https://player.vimeo.com/video/${vimeoId.replace(
        '/videos/',
        ''
      )}`
    },
    viewVideoette (videoette) {
      this.setSelectedVideoette(videoette)
      this.showVideoetteViewModal = true
    },

    shareVideoette (videoette) {
      this.setSelectedVideoette(videoette)
      this.showVideoetteShareModal = true
    },

    setSelectedVideoette (videoette) {
      this.selectedVideoette = videoette
    },

    formatCreatedAt (timestamp) {
      if (!timestamp) {
        return false
      }

      let localTime = moment.unix(timestamp).local()

      return {
        date: localTime.format('Do MMMM YYYY'),
        time: localTime.format('HH:mm')
      }
    },

    keepVideo (video, index) {
      var videoData = {
        video_id: video.id
      }
      api.post('/keep-branded-video', videoData)
        .then(response => {
          if (response.status === 200) {
            this.displayToastNotification({
              type: 'success',
              icon: 'fa-check',
              title: 'Video Kept for 3 Months',
              info: `Video has been successfully kept for another 3 months.`
            })
            // remove video from expiring video
            this.expiringVideos.splice(index, 1)
          }
        }).catch(({ response }) => {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-exclamation',
            title: 'Error',
            info: response.data.message
          })
        })
    },

    moneyFormat,
    truncate
  },

  created () {
    this.fetchExpiringVideos()
    this.fetchVideoettes()
  },

  watch: {
    showVideoetteViewModal (val) {
      if (val === false) {
        this.selectedVideoette(null)
      }
    },

    showVideoetteShareModal (val) {
      if (val === false) {
        this.selectedVideoette(null)
      }
    },
    selectedOffice () {
      this.currentPage = 1
      this.fetchVideoettes()
    },

    department () {
      this.currentPage = 1
      this.fetchVideoettes()
    },

    status () {
      this.currentPage = 1
      this.fetchVideoettes()
    },

    search () {
      this.currentPage = 1
      this.fetchVideoettes()
    }
  }
}
</script>

<style lang="sass" scoped>
</style>
